import { create } from 'zustand'

type Theme = 'dark' | 'light' | 'system'

/** The concrete theme applied to the DOM (never 'system'). */
export type ResolvedTheme = 'dark' | 'light'

const THEME_STORAGE_KEY = 'iris-ng-theme'
const SIDEBAR_STORAGE_KEY = 'iris-ng-sidebar-collapsed'

/**
 * Resolve the OS-level color scheme preference into 'dark' or 'light'.
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark'
}

/**
 * Resolve the initial theme:
 * 1. Check localStorage for persisted preference
 * 2. Default to 'system'
 */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'

  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'dark' || stored === 'light' || stored === 'system') {
    return stored
  }

  // Legacy: no stored preference — default to 'dark'
  return 'dark'
}

/**
 * Resolve the initial sidebar collapsed state from localStorage.
 * Defaults to false (expanded).
 */
function getInitialSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
}

interface UIState {
  theme: Theme
  /** The concrete theme applied to the DOM ('dark' | 'light'). When theme is 'system', this tracks the OS preference. */
  resolvedTheme: ResolvedTheme
  sidebarCollapsed: boolean
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
}

/** Handle for the current matchMedia listener so we can remove it on theme change. */
let systemMediaCleanup: (() => void) | null = null

/**
 * Install a matchMedia listener that updates resolvedTheme when the OS
 * color-scheme preference changes. Only active when theme === 'system'.
 */
function installSystemListener(set: (partial: Partial<UIState>) => void): void {
  removeSystemListener()
  if (typeof window === 'undefined') return

  const mql = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = (e: MediaQueryListEvent) => {
    set({ resolvedTheme: e.matches ? 'dark' : 'light' })
  }
  mql.addEventListener('change', handler)
  systemMediaCleanup = () => mql.removeEventListener('change', handler)
}

function removeSystemListener(): void {
  if (systemMediaCleanup) {
    systemMediaCleanup()
    systemMediaCleanup = null
  }
}

const initialTheme = getInitialTheme()

export const useUIStore = create<UIState>()((set) => {
  // If starting in 'system' mode, install the listener immediately
  if (initialTheme === 'system') {
    // Defer to after store creation so `set` is available
    queueMicrotask(() => installSystemListener(set))
  }

  return {
    theme: initialTheme,
    resolvedTheme: initialTheme === 'system' ? getSystemTheme() : initialTheme,
    sidebarCollapsed: getInitialSidebarCollapsed(),

    setTheme: (theme) => {
      if (theme === 'system') {
        installSystemListener(set)
        set({ theme, resolvedTheme: getSystemTheme() })
      } else {
        removeSystemListener()
        set({ theme, resolvedTheme: theme })
      }
    },

    toggleTheme: () =>
      set((s) => {
        const order: Theme[] = ['dark', 'light', 'system']
        const idx = order.indexOf(s.theme)
        const next = order[(idx + 1) % order.length]
        if (next === 'system') {
          queueMicrotask(() => installSystemListener(set))
          return { theme: next, resolvedTheme: getSystemTheme() }
        }
        removeSystemListener()
        return { theme: next, resolvedTheme: next }
      }),

    toggleSidebar: () =>
      set((s) => {
        const next = !s.sidebarCollapsed
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
        return { sidebarCollapsed: next }
      }),

    setSidebarCollapsed: (collapsed) => {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed))
      set({ sidebarCollapsed: collapsed })
    },
  }
})
